<template>
    <kiosk-layout>
        <div>
            <page-callout section="kiosk-tcr-campaign" class="mb-4" />
            <page-header>
                <span class="flex items-center gap-2">
                    <vx-badge color="slate" size="xs" class="font-mono">{{ props.campaignId }}</vx-badge>
                    <a
                        v-if="details"
                        :href="`/kiosk/ten-dlc/brands/${details.campaign.brand_id}`"
                        class="text-base font-normal text-slate-500 underline"
                        data-test="brand-link"
                    >
                        {{ details.campaign.brand_id }}
                    </a>
                </span>

                <template v-slot:actions>
                    <vx-button
                        @click="editModal = true"
                        size="lg"
                        color="muted-dark"
                        data-test="edit"
                        :disabled="!details || expired"
                    >
                        Edit <font-awesome-icon widthAuto :icon="faPen" />
                    </vx-button>
                    <vx-button
                        @click="share()"
                        size="lg"
                        :color="shareAllowed ? 'primary' : 'muted-dark'"
                        data-test="share"
                        :disabled="!shareAllowed"
                        :loading="sharing"
                    >
                        Re-share to Bandwidth <font-awesome-icon widthAuto :icon="faShareNodes" />
                    </vx-button>
                    <vx-button
                        @click="resubmitModal = true"
                        size="lg"
                        color="muted-dark"
                        data-test="resubmit"
                        :disabled="!details || expired"
                    >
                        Resubmit to Bandwidth <font-awesome-icon widthAuto :icon="faArrowsRotate" />
                    </vx-button>
                </template>
            </page-header>

            <page-loading v-if="loading"></page-loading>

            <div v-else-if="details" class="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <vx-expandable-card data-test="status-card">
                    <template v-slot:content>
                        <div class="text-xl font-extrabold mb-4">Status</div>
                        <vx-alert v-if="expired" color="slate" :closeable="false" class="mb-4" data-test="expired">
                            This campaign is deactivated at TCR and cannot be changed.
                        </vx-alert>
                        <dl class="flex flex-col gap-3 text-sm">
                            <div>
                                <dt class="font-bold">TCR status</dt>
                                <dd class="flex items-center gap-2">
                                    <vx-badge
                                        :color="'ACTIVE' === details.campaign.status ? 'info' : 'slate'"
                                        size="xs"
                                        data-test="tcr-status"
                                    >
                                        {{ details.campaign.status ?? '—' }}
                                    </vx-badge>
                                    <span class="text-slate-500">Active means submitted, not approved</span>
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">DCA complete</dt>
                                <dd>
                                    <vx-badge
                                        v-if="details.dca_completed_at"
                                        color="success"
                                        size="xs"
                                        data-test="dca-complete"
                                        :title="dcaHint"
                                    >
                                        Yes · {{ shortDateTime(details.dca_completed_at) }}
                                    </vx-badge>
                                    <vx-badge v-else color="danger" size="xs" data-test="dca-complete" :title="dcaHint"
                                        >No</vx-badge
                                    >
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Next renewal</dt>
                                <dd class="text-slate-500">
                                    {{
                                        details.campaign.next_renewal_or_expiration_date
                                            ? shortDateTime(details.campaign.next_renewal_or_expiration_date)
                                            : '—'
                                    }}
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Monthly fee</dt>
                                <dd class="text-slate-500">{{ feeLabel(details.campaign.monthly_fee) }}</dd>
                            </div>
                            <div>
                                <dt class="font-bold">Reference id</dt>
                                <dd class="text-slate-500">
                                    <span v-if="details.campaign.reference_id" class="font-mono">{{
                                        details.campaign.reference_id
                                    }}</span>
                                    <span v-else>—</span>
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Numbers in the mirror</dt>
                                <dd class="text-slate-500">
                                    {{ details.phone_numbers_count }}
                                    <a :href="phoneNumbersHref" class="ml-2 underline" data-test="phone-numbers-link"
                                        >View numbers</a
                                    >
                                </dd>
                            </div>
                        </dl>
                        <div class="mt-4 flex items-center gap-2">
                            <vx-button
                                @click="deactivateModal = true"
                                size="sm"
                                outline
                                color="danger"
                                data-test="deactivate"
                                :disabled="!deactivateAllowed"
                            >
                                Deactivate <font-awesome-icon widthAuto :icon="faBan" />
                            </vx-button>
                            <template v-if="!expired && !deactivateAllowed">
                                <span class="text-sm text-slate-500" data-test="deactivate-hint">
                                    Detach its numbers first
                                </span>
                                <vx-button @click="load()" size="sm" color="muted-dark" data-test="check-again">
                                    Check again
                                </vx-button>
                            </template>
                        </div>
                    </template>
                </vx-expandable-card>

                <kiosk-tcr-campaign-sharing-card :sharing="details.sharing.shared_by_me" />

                <kiosk-tcr-campaign-carrier-table
                    :operationStatus="details.operation_status"
                    :carrierTerms="details.carrier_terms"
                    :sharingStatus="details.sharing.shared_by_me?.sharing_status ?? null"
                    class="xl:col-span-2"
                />

                <vx-expandable-card class="xl:col-span-2" data-test="bandwidth-card">
                    <template v-slot:content>
                        <div class="text-xl font-extrabold mb-4">Bandwidth</div>
                        <vx-alert
                            v-if="details.bandwidth.error"
                            color="danger"
                            :closeable="false"
                            class="mb-4"
                            data-test="bandwidth-error"
                        >
                            {{ details.bandwidth.error }}
                        </vx-alert>
                        <vx-alert
                            v-if="importPending"
                            color="info"
                            :closeable="false"
                            class="mb-4"
                            data-test="import-pending"
                        >
                            <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <span>
                                    Bandwidth accepted the share but holds no record yet; the import runs about ten
                                    minutes after Bandwidth accepts
                                </span>
                                <vx-button @click="resubmitModal = true" size="sm" color="info" data-test="import">
                                    Import into Bandwidth
                                </vx-button>
                            </div>
                        </vx-alert>
                        <template v-if="details.bandwidth.campaign">
                            <dl class="flex flex-wrap gap-x-8 gap-y-3 text-sm">
                                <div>
                                    <dt class="font-bold">Bandwidth id</dt>
                                    <dd class="text-slate-500">
                                        <span v-if="details.bandwidth.campaign.bandwidth_id" class="font-mono">
                                            {{ details.bandwidth.campaign.bandwidth_id }}
                                        </span>
                                        <span v-else>—</span>
                                    </dd>
                                </div>
                                <div>
                                    <dt class="font-bold">Status</dt>
                                    <dd class="text-slate-500" data-test="bandwidth-status">
                                        {{ details.bandwidth.campaign.status ?? '—' }}
                                    </dd>
                                </div>
                                <div>
                                    <dt class="font-bold">Vetting status</dt>
                                    <dd class="text-slate-500">
                                        {{ details.bandwidth.campaign.vetting_status ?? '—' }}
                                    </dd>
                                </div>
                                <div>
                                    <dt class="font-bold">Imported</dt>
                                    <dd class="text-slate-500" data-test="bandwidth-imported">
                                        {{ yesNo(details.bandwidth.campaign.imported) }}
                                    </dd>
                                </div>
                                <div>
                                    <dt class="font-bold">Number pool</dt>
                                    <dd class="text-slate-500">{{ yesNo(details.bandwidth.campaign.number_pool) }}</dd>
                                </div>
                                <div>
                                    <dt class="font-bold">Modified</dt>
                                    <dd class="text-slate-500">
                                        {{
                                            details.bandwidth.campaign.modified_date
                                                ? shortDateTime(details.bandwidth.campaign.modified_date)
                                                : '—'
                                        }}
                                    </dd>
                                </div>
                            </dl>
                            <vx-table
                                v-if="details.bandwidth.campaign.approvals.length"
                                class="xl:grid-cols-5 mt-4"
                                rowBreakpoint="xl"
                                data-test="approvals"
                            >
                                <vx-table-row isHeader>
                                    <div>Partner</div>
                                    <div>Decision</div>
                                    <div>Reason</div>
                                    <div>Class / TPM</div>
                                    <div>Tier / Daily cap</div>
                                </vx-table-row>
                                <vx-table-row
                                    v-for="approval in details.bandwidth.campaign.approvals"
                                    :key="approval.partner_id"
                                    :data-test="`approval-${approval.partner_id}`"
                                >
                                    <vx-table-row-title>{{ approval.partner_id }}</vx-table-row-title>
                                    <vx-table-row-item>
                                        <template v-slot:label> Decision: </template>
                                        <vx-badge
                                            :color="approvalDecisionColors[approval.decision] ?? 'slate'"
                                            size="xs"
                                        >
                                            {{ approvalDecisionLabels[approval.decision] ?? approval.decision }}
                                        </vx-badge>
                                    </vx-table-row-item>
                                    <vx-table-row-item>
                                        <template v-slot:label> Reason: </template>
                                        {{ approval.reason ?? '—' }}
                                    </vx-table-row-item>
                                    <vx-table-row-item>
                                        <template v-slot:label> Class / TPM: </template>
                                        {{ approval.message_class ?? '—' }} / {{ approval.tpm ?? '—' }}
                                    </vx-table-row-item>
                                    <vx-table-row-item>
                                        <template v-slot:label> Tier / Daily cap: </template>
                                        {{ approval.brand_tier ?? '—' }} / {{ approval.brand_daily_cap ?? '—' }}
                                    </vx-table-row-item>
                                </vx-table-row>
                            </vx-table>
                        </template>
                        <div
                            v-else-if="!details.bandwidth.error && !importPending"
                            class="text-sm text-slate-500"
                            data-test="not-imported"
                        >
                            Not imported into Bandwidth yet
                        </div>
                    </template>
                </vx-expandable-card>

                <kiosk-tcr-campaign-numbers-card
                    :campaignId="props.campaignId"
                    :expired="expired"
                    class="xl:col-span-2"
                    data-test="numbers-card"
                />

                <vx-expandable-card data-test="packet-card">
                    <template v-slot:content>
                        <div class="text-xl font-extrabold mb-4">Packet</div>
                        <dl class="flex flex-col gap-3 text-sm">
                            <div>
                                <dt class="font-bold">Description</dt>
                                <dd class="text-slate-500 whitespace-pre-wrap">
                                    {{ details.campaign.description ?? '—' }}
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Message flow</dt>
                                <dd class="text-slate-500 whitespace-pre-wrap">
                                    {{ details.campaign.message_flow ?? '—' }}
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Samples</dt>
                                <dd>
                                    <ol class="list-decimal pl-5 text-slate-500 whitespace-pre-wrap">
                                        <li v-for="(sample, index) in details.campaign.samples ?? []" :key="index">
                                            {{ sample }}
                                        </li>
                                    </ol>
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Opt-in</dt>
                                <dd class="text-slate-500">
                                    {{ details.campaign.optin_keywords || '—' }} ·
                                    {{ details.campaign.optin_message || '—' }}
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Opt-out</dt>
                                <dd class="text-slate-500">
                                    {{ details.campaign.optout_keywords || '—' }} ·
                                    {{ details.campaign.optout_message || '—' }}
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Help</dt>
                                <dd class="text-slate-500">
                                    {{ details.campaign.help_keywords || '—' }} ·
                                    {{ details.campaign.help_message || '—' }}
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Privacy policy</dt>
                                <dd class="text-slate-500 break-all">
                                    {{ details.campaign.privacy_policy_link || '—' }}
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Terms and conditions</dt>
                                <dd class="text-slate-500 break-all">
                                    {{ details.campaign.terms_and_conditions_link || '—' }}
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Attributes</dt>
                                <dd class="flex flex-wrap gap-1">
                                    <vx-badge
                                        v-for="attribute in attributes"
                                        :key="attribute.label"
                                        size="xs"
                                        :color="attribute.value ? 'success' : 'slate'"
                                    >
                                        {{ attribute.label }}: {{ yesNo(attribute.value) }}
                                    </vx-badge>
                                </dd>
                            </div>
                        </dl>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card data-test="documents-card">
                    <template v-slot:content>
                        <div class="flex items-center justify-between gap-2 mb-4">
                            <span class="text-xl font-extrabold">Supporting documents</span>
                            <input
                                type="file"
                                ref="screenshotInput"
                                class="opacity-0 absolute w-1 h-1"
                                :accept="IMAGE_ACCEPT"
                                aria-hidden="true"
                                data-test="upload-screenshot-input"
                                :disabled="expired"
                                @change="onScreenshotPicked"
                            />
                            <vx-button
                                @click="screenshotInput?.click()"
                                size="sm"
                                color="primary"
                                data-test="upload-screenshot"
                                :disabled="expired || uploading"
                                :loading="uploading"
                            >
                                Attach screenshot <font-awesome-icon widthAuto :icon="faUpload" />
                            </vx-button>
                        </div>
                        <vx-alert
                            v-if="!details.supporting_documents.length"
                            color="danger"
                            :closeable="false"
                            data-test="missing-screenshot"
                        >
                            No opt-in screenshot is attached.
                            <template v-if="!expired">Attach it here.</template>
                        </vx-alert>
                        <vx-table v-else class="xl:grid-cols-3-auto" rowBreakpoint="xl">
                            <vx-table-row isHeader>
                                <div>File</div>
                                <div>Type</div>
                            </vx-table-row>
                            <vx-table-row
                                v-for="attachment in details.supporting_documents"
                                :key="attachment.uuid"
                                :data-test="`document-${attachment.uuid}`"
                            >
                                <vx-table-row-title>
                                    <span :title="attachment.uuid">{{ attachment.file_name ?? '—' }}</span>
                                </vx-table-row-title>
                                <vx-table-row-item>
                                    <template v-slot:label> Type: </template>
                                    {{ attachment.mime_type ?? '—' }}
                                </vx-table-row-item>
                                <vx-table-row-buttons>
                                    <vx-button
                                        @click="removing = attachment"
                                        size="sm"
                                        outline
                                        color="danger"
                                        data-test="remove-document"
                                        :disabled="expired"
                                    >
                                        Remove <font-awesome-icon widthAuto :icon="faTrash" />
                                    </vx-button>
                                </vx-table-row-buttons>
                            </vx-table-row>
                        </vx-table>
                        <div v-if="screenshotError" class="text-sm text-rose-600 mt-2" data-test="screenshot-error">
                            {{ screenshotError }}
                        </div>
                    </template>
                </vx-expandable-card>

                <kiosk-tcr-teams-list
                    :teams="details.teams"
                    emptyText="No Voxie numbers are attached to this campaign in the mirror yet"
                />
            </div>

            <kiosk-tcr-campaign-edit-modal
                v-if="details"
                v-model:visible="editModal"
                :campaignId="props.campaignId"
                :campaign="details.campaign"
                @updated="load()"
            />

            <kiosk-tcr-campaign-deactivate-modal v-model:visible="deactivateModal" :campaignId="props.campaignId" />

            <kiosk-tcr-campaign-document-remove-modal
                v-if="removing"
                v-model:visible="removeModal"
                :campaignId="props.campaignId"
                :attachment="removing"
                @removed="onDocumentRemoved"
            />

            <vx-modal v-model:visible="resubmitModal" size="sm" padding="standard">
                <template v-slot:header>Resubmit to Bandwidth</template>
                <template v-slot:subheader>{{ props.campaignId }}</template>

                <div class="text-sm text-slate-800">
                    <strong>
                        Resubmitting more than once can trigger extra vetting; make every edit first, then resubmit
                        once.
                    </strong>
                    <div class="mt-2">Bandwidth re-reviews the campaign as it stands in TCR now.</div>
                </div>

                <template v-slot:footer>
                    <div class="flex flex-col-reverse gap-4 lg:flex-row">
                        <vx-button
                            @click="resubmitModal = false"
                            type="button"
                            class="grow lg:w-20 lg:grow-0"
                            size="lg"
                            color="muted"
                        >
                            Cancel
                        </vx-button>
                        <vx-button
                            @click="resubmit()"
                            data-test="confirm-resubmit"
                            :loading="resubmitting"
                            type="button"
                            class="grow"
                            size="lg"
                            color="primary"
                        >
                            Resubmit
                        </vx-button>
                    </div>
                </template>
            </vx-modal>
        </div>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { faArrowsRotate, faBan, faPen, faShareNodes, faTrash, faUpload } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxBadge,
    VxButton,
    VxExpandableCard,
    VxModal,
    VxTable,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import PageCallout from '~/components/general/PageCallout.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import { useOptInScreenshot } from '~/components/kiosk/kiosk-ten-dlc/composables/useOptInScreenshot';
import KioskTcrCampaignCarrierTable from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrCampaignCarrierTable.vue';
import KioskTcrCampaignDeactivateModal from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrCampaignDeactivateModal.vue';
import KioskTcrCampaignDocumentRemoveModal from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrCampaignDocumentRemoveModal.vue';
import KioskTcrCampaignEditModal from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrCampaignEditModal.vue';
import KioskTcrCampaignNumbersCard from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrCampaignNumbersCard.vue';
import KioskTcrCampaignSharingCard from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrCampaignSharingCard.vue';
import KioskTcrTeamsList from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrTeamsList.vue';
import { IMAGE_ACCEPT } from '~/components/kiosk/kiosk-ten-dlc/registration/registrationImage';
import {
    approvalDecisionColors,
    approvalDecisionLabels,
    feeLabel,
    yesNo,
} from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import type { TcrLiveCampaignDetails, TcrSupportingDocument } from '~/services/adminTenDlcRegistrationClient.types';
import { shortDateTime } from '~/utils/date';
import { catchRequestError } from '~/utils/request';
import { stringifyQueryString } from '~/utils/string';

const dcaHint = "Numbers cannot be attached until this is Yes; it arrives on TCR's CAMPAIGN_DCA_COMPLETE webhook";

const props = defineProps<{ campaignId: string }>();

const toasted = useToasted();
const { screenshot, screenshotError, onScreenshotChange } = useOptInScreenshot();

const details = ref<TcrLiveCampaignDetails | null>(null);
const loading = ref(true);
const editModal = ref(false);
const resubmitModal = ref(false);
const deactivateModal = ref(false);
const removing = ref<TcrSupportingDocument>();
const sharing = ref(false);
const resubmitting = ref(false);
const uploading = ref(false);
const screenshotInput = ref<HTMLInputElement>();

const load = () => {
    loading.value = true;
    adminTenDlcRegistrationClient
        .getCampaign(props.campaignId)
        .then((response) => {
            details.value = response.data;
        })
        .catch((e) => {
            toasted.error(catchRequestError(e), { sticky: true });
        })
        .finally(() => {
            loading.value = false;
        });
};

const expired = computed(() => 'EXPIRED' === details.value?.campaign.status);
const sharedByMe = computed(() => details.value?.sharing.shared_by_me ?? null);
const shareAllowed = computed(
    () =>
        null !== details.value &&
        !expired.value &&
        (null === sharedByMe.value || 'DECLINED' === sharedByMe.value.sharing_status)
);
// Bandwidth being unreachable says nothing about the import, so the prompt waits for a clean read.
const importPending = computed(
    () =>
        !expired.value &&
        null === (details.value?.bandwidth.error ?? null) &&
        'ACCEPTED' === sharedByMe.value?.sharing_status &&
        true !== details.value?.bandwidth.campaign?.imported
);
const deactivateAllowed = computed(
    () => null !== details.value && !expired.value && 0 === details.value.phone_numbers_count
);
const removeModal = computed({
    get: () => undefined !== removing.value,
    set: (open: boolean) => {
        if (!open) {
            removing.value = undefined;
        }
    },
});
const phoneNumbersHref = computed(
    () => `/kiosk/ten-dlc/phone-numbers?${stringifyQueryString({ campaign_id: props.campaignId })}`
);
const attributes = computed(() => {
    const campaign = details.value?.campaign;

    if (!campaign) {
        return [];
    }

    return [
        { label: 'Embedded link', value: campaign.embedded_link },
        { label: 'Embedded phone', value: campaign.embedded_phone },
        { label: 'Number pool', value: campaign.number_pool },
        { label: 'Age gated', value: campaign.age_gated },
        { label: 'Direct lending', value: campaign.direct_lending },
        { label: 'Auto-renewal', value: campaign.auto_renewal },
        { label: 'Subscriber opt-in', value: campaign.subscriber_optin },
        { label: 'Subscriber opt-out', value: campaign.subscriber_optout },
        { label: 'Subscriber help', value: campaign.subscriber_help },
    ];
});

const share = async () => {
    sharing.value = true;
    try {
        await adminTenDlcRegistrationClient.shareCampaign(props.campaignId);
        toasted.success('Shared to Bandwidth.');
        load();
    } catch (e) {
        toasted.error(catchRequestError(e), { sticky: true });
    } finally {
        sharing.value = false;
    }
};

const resubmit = async () => {
    resubmitting.value = true;
    try {
        const response = await adminTenDlcRegistrationClient.resubmitCampaign(props.campaignId);
        toasted.success(
            null === response.data.bandwidth_id
                ? 'Resubmitted to Bandwidth.'
                : `Resubmitted to Bandwidth as ${response.data.bandwidth_id}.`
        );
        resubmitModal.value = false;
        load();
    } catch (e) {
        toasted.error(catchRequestError(e), { sticky: true });
    } finally {
        resubmitting.value = false;
    }
};

const onScreenshotPicked = async (event: Event) => {
    uploading.value = true;
    try {
        await onScreenshotChange(event);

        if (!screenshot.value) {
            return;
        }

        await adminTenDlcRegistrationClient.uploadSupportingDocument(props.campaignId, screenshot.value);
        toasted.success('Screenshot attached.');
        load();
    } catch (e) {
        toasted.error(catchRequestError(e), { sticky: true });
    } finally {
        uploading.value = false;
    }
};

const onDocumentRemoved = () => {
    removing.value = undefined;
    toasted.success('Document removed.');
    load();
};

load();
</script>
